// SystemCalculatedInfo.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUserTie,
  FaLayerGroup,
  FaProjectDiagram,
  FaCheckCircle,
  FaClock,
  FaEdit,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { API_BASE } from "../../../ApiUrl";
import "./SystemCalculatedInfo.css";

/* ------------------------------------------------------------------ */
/*  CalculatedFieldCode.choices                                        */
/* ------------------------------------------------------------------ */
export const CALCULATED_FIELD_CODES = {
  CALENDAR_EXPERIENCE: "Calendar experience",
  VERIFIED_FIELD_DAYS: "Verified field days",
  VERIFIED_PROJECT_COUNT: "Verified project count",
  HIGHEST_AUTHORITY_REACHED: "Highest authority reached",
  QUALION_LEVEL: "Qualion level (L0-L5)",
  PROFESSIONAL_HEADLINE: "Professional headline",
  PROFESSIONAL_SUMMARY: "Professional summary",
  PRIMARY_ROLE: "Primary role",
  ADDITIONAL_ROLES: "Additional roles",
  INDUSTRIES_SERVED: "Industries served",
  PROJECT_RESPONSIBILITY_BULLETS: "Project responsibility bullets",
  TOTAL_CAREER_EXPERIENCE: "Total career experience",
  DEPLOYABILITY_FLAG: "Deployability flag",
  CREDENTIAL_STATUS: "Credential status",
  CANDIDATE_MENTOR_CLASSIFICATION: "Candidate/Mentor classification",
};

/* ------------------------------------------------------------------ */
/*  OverrideReasonCode.choices                                         */
/* ------------------------------------------------------------------ */
export const OVERRIDE_REASON_CODES = {
  SOURCE_DATA_INCORRECT: "Source data incorrect",
  SOURCE_DATA_INCOMPLETE: "Source data incomplete",
  RULE_DOES_NOT_FIT_SITUATION:
    "Automated rule does not represent the professional situation",
  EVIDENCE_RECEIVED_LATE: "Additional evidence received after calculation",
  SYSTEM_DEFECT: "System/calculation defect",
  OTHER: "Other",
};

/* ------------------------------------------------------------------ */
/*  UI field -> CalculatedFieldCode                                    */
/* ------------------------------------------------------------------ */
const FIELD_CODES = {
  headline: "PROFESSIONAL_HEADLINE",
  summary: "PROFESSIONAL_SUMMARY",
  primary_role: "PRIMARY_ROLE",
  additional_roles: "ADDITIONAL_ROLES",
  industries_served: "INDUSTRIES_SERVED",
  total_career_experience_months: "TOTAL_CAREER_EXPERIENCE",
  current_classification: "CANDIDATE_MENTOR_CLASSIFICATION",

  calendar_experience_months: "CALENDAR_EXPERIENCE",
  verified_field_days: "VERIFIED_FIELD_DAYS",
  verified_project_count: "VERIFIED_PROJECT_COUNT",
  highest_authority_reached: "HIGHEST_AUTHORITY_REACHED",
  current_qualion_level: "QUALION_LEVEL",
  is_deployable: "DEPLOYABILITY_FLAG",

  status: "CREDENTIAL_STATUS",
  responsibilities: "PROJECT_RESPONSIBILITY_BULLETS",
};

/* ------------------------------------------------------------------ */
/*  Read tenant_user from localStorage                                 */
/* ------------------------------------------------------------------ */
const getTenantUser = () => {
  try {
    const raw = localStorage.getItem("tenant_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------ */
/*  Editable Info Row  (per-field reason code + reason)                */
/* ------------------------------------------------------------------ */
const EditableInfoRow = ({ label, value, multiline = false, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(
    Array.isArray(value) ? value.join(", ") : value ?? ""
  );
  const [rowReasonCode, setRowReasonCode] = useState("SOURCE_DATA_INCOMPLETE");
  const [rowReason, setRowReason] = useState("");
  const [rowError, setRowError] = useState("");

  const startEdit = () => {
    setDraft(Array.isArray(value) ? value.join(", ") : value ?? "");
    setRowReasonCode("SOURCE_DATA_INCOMPLETE");
    setRowReason("");
    setRowError("");
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = () => {
    if (!rowReasonCode) {
      setRowError("Reason code is required.");
      return;
    }
    if (!rowReason.trim()) {
      setRowError("Reason is required.");
      return;
    }
    onSave(draft, { reason_code: rowReasonCode, reason: rowReason.trim() });
    setEditing(false);
  };

  const display =
    value === null || value === undefined || value === "" ? "—" : value;

  return (
    <div className={`sci-info-row ${multiline ? "sci-info-row-block" : ""}`}>
      <span className="sci-info-label">{label}</span>

      {editing ? (
        <div className="sci-edit-wrap">
          {multiline ? (
            <textarea
              className="sci-input sci-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
            />
          ) : (
            <input
              className="sci-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          )}

          <label className="sci-edit-field">
            <span className="sci-edit-field-label">Reason Code</span>
            <select
              className="sci-input"
              value={rowReasonCode}
              onChange={(e) => setRowReasonCode(e.target.value)}
            >
              {Object.entries(OVERRIDE_REASON_CODES).map(([code, lbl]) => (
                <option key={code} value={code}>
                  {code} — {lbl}
                </option>
              ))}
            </select>
          </label>

          <label className="sci-edit-field">
            <span className="sci-edit-field-label">Reason</span>
            <textarea
              className="sci-input sci-textarea"
              rows={2}
              value={rowReason}
              onChange={(e) => setRowReason(e.target.value)}
              placeholder="Explain why this value is being changed…"
            />
          </label>

          {rowError && <div className="sci-edit-error">{rowError}</div>}

          <div className="sci-edit-actions">
            <button className="sci-btn sci-btn-save" onClick={save} type="button">
              <FaSave /> Save
            </button>
            <button
              className="sci-btn sci-btn-cancel"
              onClick={cancel}
              type="button"
            >
              <FaTimes /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <span className={`sci-info-value ${multiline ? "sci-pre-wrap" : ""}`}>
          {display}
          <button
            type="button"
            className="sci-edit-icon"
            onClick={startEdit}
            title="Edit"
          >
            <FaEdit />
          </button>
        </span>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const StatusBadge = ({ status }) => {
  const isVerified = /VERIFIED|APPROVED|ACTIVE|COMPLETE/i.test(status || "");
  return (
    <span
      className={`sci-badge ${isVerified ? "sci-badge-success" : "sci-badge-warn"}`}
    >
      {isVerified ? <FaCheckCircle /> : <FaClock />}
      {status || "N/A"}
    </span>
  );
};

const SectionCard = ({ title, icon, count, children }) => (
  <section className="sci-section-card">
    <header className="sci-section-head">
      <span className="sci-section-icon">{icon}</span>
      <h3>{title}</h3>
      {typeof count === "number" && (
        <span className="sci-count-badge">{count}</span>
      )}
    </header>
    <div className="sci-section-content">{children}</div>
  </section>
);

const RecordCard = ({ heading, badge, children }) => (
  <div className="sci-record-card">
    <div className="sci-record-head">
      <h4>{heading}</h4>
      {badge}
    </div>
    <div className="sci-record-body">{children}</div>
  </div>
);

const renderList = (value) => {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  return value ?? "—";
};

const renderDeployable = (value) => {
  if (value === "DEPLOYABLE") return "Yes";
  if (value === "NOT_DEPLOYABLE") return "No";
  return value ?? "—";
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
const SystemCalculatedInfo = ({ professionalId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [overrides, setOverrides] = useState({});
  const [rulesetVersion, setRulesetVersion] = useState("2026.1");
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    if (professionalId) fetchRelatedRecords(professionalId);
  }, [professionalId]);

  const fetchRelatedRecords = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE}/professional-profiles/${id}/related-records/`
      );
      if (res.data.success) setData(res.data.data);
      else setError("Failed to load system calculated info.");
    } catch (err) {
      console.error("Error fetching related records:", err);
      setError("Failed to load system calculated info.");
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------------------------------------
     handleChange now accepts projectRecordId and stores it so it
     can be included in the payload for project-level overrides.
     -------------------------------------------------------------- */
  const handleChange = ({
    code,
    value,
    reason_code,
    reason,
    scopeId = null,
    projectId = null,
    projectRecordId = null,
  }) => {
    if (!code) return;
    const key = `${code}|${scopeId ?? ""}|${projectId ?? ""}`;
    setOverrides((prev) => ({
      ...prev,
      [key]: {
        calculation_field_code: code,
        professional_scope_id: scopeId,
        project_id: projectId,
        project_record_id: projectRecordId,
        value,
        reason_code,
        reason,
      },
    }));
    setSuccessMsg("");
  };

  const submitOverrides = async () => {
    const tenantUser = getTenantUser();
    if (!tenantUser) {
      alert("Tenant user not found in local storage. Please log in again.");
      return;
    }
    const { tenant_id } = tenantUser;

    const fields = Object.values(overrides).map((o) => ({
      calculation_field_code: o.calculation_field_code,
      ...(o.professional_scope_id != null && {
        professional_scope_id: o.professional_scope_id,
      }),
      ...(o.project_record_id != null && {
        project_record_id: o.project_record_id,
      }),
      value: o.value,
      reason_code: o.reason_code,
      reason: o.reason,
    }));

    if (fields.length === 0) {
      alert("No changes to submit.");
      return;
    }

    const payload = {
      professional_profile_id: professionalId,
      updated_by: tenant_id,
      system_ruleset_version: rulesetVersion,
      fields,
    };

    try {
      setSaving(true);
      const res = await axios.post(
        `${API_BASE}/calculated-fields/admin-verification/`,
        payload
      );
      if (res.data?.success || res.status === 200 || res.status === 201) {
        setSuccessMsg("Calculated fields updated successfully.");
        setOverrides({});
        fetchRelatedRecords(professionalId);
      } else {
        setError(
          res.data?.message ||
            JSON.stringify(res.data?.errors) ||
            "Failed to submit overrides."
        );
      }
    } catch (err) {
      console.error("Error submitting overrides:", err);
      const serverErrors = err?.response?.data?.errors;
      setError(
        err?.response?.data?.message ||
          (serverErrors ? JSON.stringify(serverErrors) : null) ||
          err?.response?.data?.detail ||
          "Failed to submit overrides."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="sci-loading">
        <div className="sci-spinner"></div>
        <p>Loading system calculated info...</p>
      </div>
    );
  }

  if (error) return <div className="sci-error">{error}</div>;
  if (!data)
    return <div className="sci-empty">No system calculated data available.</div>;

  const scopes = data.scopes || [];
  const projectRecords = data.project_records || [];
  const pendingCount = Object.keys(overrides).length;

  return (
    <div className="sci-container">
      {/* ---- Professional Profile ---- */}
      <SectionCard title="Professional Profile" icon={<FaUserTie />}>
        <EditableInfoRow
          label="Headline"
          value={data.headline}
          onSave={(v, meta) =>
            handleChange({ code: FIELD_CODES.headline, value: v, ...meta })
          }
        />
        <EditableInfoRow
          label="Summary"
          value={data.summary}
          multiline
          onSave={(v, meta) =>
            handleChange({ code: FIELD_CODES.summary, value: v, ...meta })
          }
        />
        <EditableInfoRow
          label="Primary Role"
          value={renderList(data.primary_role)}
          onSave={(v, meta) =>
            handleChange({ code: FIELD_CODES.primary_role, value: v, ...meta })
          }
        />
        <EditableInfoRow
          label="Additional Roles"
          value={renderList(data.additional_roles)}
          onSave={(v, meta) =>
            handleChange({
              code: FIELD_CODES.additional_roles,
              value: v.split(",").map((s) => s.trim()).filter(Boolean),
              ...meta,
            })
          }
        />
        <EditableInfoRow
          label="Industries Served"
          value={renderList(data.industries_served)}
          onSave={(v, meta) =>
            handleChange({
              code: FIELD_CODES.industries_served,
              value: v.split(",").map((s) => s.trim()).filter(Boolean),
              ...meta,
            })
          }
        />
        <EditableInfoRow
          label="Total Career Experience (Months)"
          value={data.total_career_experience_months}
          onSave={(v, meta) =>
            handleChange({
              code: FIELD_CODES.total_career_experience_months,
              value: Number(v) || v,
              ...meta,
            })
          }
        />
        <EditableInfoRow
          label="Current Classification"
          value={data.current_classification}
          onSave={(v, meta) =>
            handleChange({
              code: FIELD_CODES.current_classification,
              value: v,
              ...meta,
            })
          }
        />
      </SectionCard>

      {/* ---- Professional Scope ---- */}
      <SectionCard
        title="Professional Scope"
        icon={<FaLayerGroup />}
        count={scopes.length}
      >
        {scopes.length === 0 ? (
          <p className="sci-empty-msg">No scope records found.</p>
        ) : (
          scopes.map((scope, idx) => (
            <RecordCard
              key={scope.id}
              heading={`Scope ${idx + 1}`}
              badge={<StatusBadge status={scope.verification_status} />}
            >
              <EditableInfoRow
                label="Calendar Experience (Months)"
                value={scope.calendar_experience_months}
                onSave={(v, meta) =>
                  handleChange({
                    code: FIELD_CODES.calendar_experience_months,
                    value: Number(v) || v,
                    scopeId: scope.id,
                    ...meta,
                  })
                }
              />
              <EditableInfoRow
                label="Verified Field Days"
                value={scope.verified_field_days}
                onSave={(v, meta) =>
                  handleChange({
                    code: FIELD_CODES.verified_field_days,
                    value: v,
                    scopeId: scope.id,
                    ...meta,
                  })
                }
              />
              <EditableInfoRow
                label="Verified Project Count"
                value={scope.verified_project_count}
                onSave={(v, meta) =>
                  handleChange({
                    code: FIELD_CODES.verified_project_count,
                    value: Number(v) || v,
                    scopeId: scope.id,
                    ...meta,
                  })
                }
              />
              <EditableInfoRow
                label="Highest Authority Reached"
                value={scope.highest_authority_reached}
                onSave={(v, meta) =>
                  handleChange({
                    code: FIELD_CODES.highest_authority_reached,
                    value: v,
                    scopeId: scope.id,
                    ...meta,
                  })
                }
              />
              <EditableInfoRow
                label="Current Qualification Level"
                value={scope.current_qualion_level}
                onSave={(v, meta) =>
                  handleChange({
                    code: FIELD_CODES.current_qualion_level,
                    value: Number(v) || v,
                    scopeId: scope.id,
                    ...meta,
                  })
                }
              />
              <EditableInfoRow
                label="Is Deployable"
                value={renderDeployable(scope.is_deployable)}
                onSave={(v, meta) =>
                  handleChange({
                    code: FIELD_CODES.is_deployable,
                    value: /^y/i.test(v) ? "DEPLOYABLE" : "NOT_DEPLOYABLE",
                    scopeId: scope.id,
                    ...meta,
                  })
                }
              />
            </RecordCard>
          ))
        )}
      </SectionCard>

      {/* ---- Project Records ---- */}
      <SectionCard
        title="Project Records"
        icon={<FaProjectDiagram />}
        count={projectRecords.length}
      >
        {projectRecords.length === 0 ? (
          <p className="sci-empty-msg">No project records found.</p>
        ) : (
          projectRecords.map((proj, idx) => (
            <RecordCard
              key={proj.id}
              heading={proj.project_name || `Project ${idx + 1}`}
              badge={<StatusBadge status={proj.status} />}
            >
              <EditableInfoRow
                label="Status"
                value={proj.status}
                onSave={(v, meta) =>
                  handleChange({
                    code: FIELD_CODES.status,
                    value: v,
                    projectId: proj.id,
                    projectRecordId: proj.id, // <-- send project_record_id
                    ...meta,
                  })
                }
              />
              <EditableInfoRow
                label="Responsibilities"
                value={proj.responsibilities}
                multiline
                onSave={(v, meta) =>
                  handleChange({
                    code: FIELD_CODES.responsibilities,
                    value: v,
                    projectId: proj.id,
                    projectRecordId: proj.id, // <-- send project_record_id
                    ...meta,
                  })
                }
              />
            </RecordCard>
          ))
        )}
      </SectionCard>

      {/* ---- Submit panel ---- */}
      {pendingCount > 0 && (
        <div className="sci-submit-panel">
          <div className="sci-submit-header">
            <strong>{pendingCount}</strong> pending change
            {pendingCount > 1 ? "s" : ""} to submit
          </div>

          <div className="sci-submit-grid">
            <label className="sci-submit-field">
              <span>Ruleset Version</span>
              <input
                value={rulesetVersion}
                onChange={(e) => setRulesetVersion(e.target.value)}
              />
            </label>
          </div>

          <ul className="sci-pending-list">
            {Object.values(overrides).map((o) => (
              <li
                key={`${o.calculation_field_code}|${o.professional_scope_id ?? ""}|${o.project_id ?? ""}`}
              >
                <span className="sci-pending-code">
                  {o.calculation_field_code}
                </span>
                <span className="sci-pending-reason">
                  {o.reason_code} — {o.reason}
                </span>
              </li>
            ))}
          </ul>

          {successMsg && <div className="sci-success">{successMsg}</div>}

          <div className="sci-submit-actions">
            <button
              type="button"
              className="sci-btn sci-btn-save"
              onClick={submitOverrides}
              disabled={saving}
            >
              <FaSave /> {saving ? "Submitting…" : "Submit Changes"}
            </button>
            <button
              type="button"
              className="sci-btn sci-btn-cancel"
              onClick={() => setOverrides({})}
              disabled={saving}
            >
              <FaTimes /> Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemCalculatedInfo;